'use client'

import { ButtonContentLink } from '@fukku/button/ContentLink'
import type { BackInStockProps } from 'back-in-stock/src/types'
import { BottomSheet } from 'fukku/Dialog/BottomSheet/BottomSheet'
import { Content } from 'fukku/Dialog/Content/Content'
import type { DialogAPI } from 'fukku/Dialog/Dialog'
import { Header } from 'fukku/Dialog/Header/Header'
import { Steps, type StepsApi } from 'fukku/Dialog/Steps/Steps'
import { useLabels } from 'labels/hooks/useLabels/useLabels'
import dynamic from 'next/dynamic'
import type { Product } from 'product/types'
import type { ProductLocations } from 'product/types/ProductLocations'
import type { Stock } from 'product/types/Stock'
import {
	Fragment,
	type Ref,
	forwardRef,
	useEffect,
	useImperativeHandle,
	useRef,
} from 'react'
import { DisclaimerMessage } from '../DisclaimerMessage/DisclaimerMessage'
import { type SelectedSize, SizesList } from '../SizesList/SizesList'
import { useBackInStock } from '../hooks/useBackInStock/useBackInStock'
import { useMessageDisclaimer } from '../hooks/useMessageDisclaimer/useMessageDisclaimer'
import { useSelectedSize } from '../hooks/useSelectedSize/useSelectedSize'

const BackInStockForm = dynamic(
	() =>
		import('../BackInStockForm/BackInStockForm').then(
			(mod) => mod.BackInStockForm
		),
	{ ssr: false }
)

export interface SheetProps {
	/** Product info */
	product: Product
	/** Product reference id (8 digits) */
	productId: string
	/** Selected color id (2 characters)*/
	selectedColorId: string
	/** Test id */
	'data-testid'?: string
	/** Callback when bottom sheet is closed */
	onClose?: () => void
	/** Callback when available size is selected */
	onSelectedSize?: (selectedSize: SelectedSize) => void
	/** Should show a sizes guide link */
	hasSizesGuide?: boolean
	/** Callback when sizes guide link is clicked */
	onSizeGuideLink?: () => void
}

export interface SheetExtendedProps extends SheetProps {
	/** Pre computed product color-sizes stock */
	productStock: Stock
	/** Message to show on sizes with no local warehouse */
	delayedMessage?: string
	/** Check if size must be considered as delayed delivery */
	delayedWareHouse?: (warehousesIds?: string[]) => boolean
	/** Where product will be added to bag */
	location: ProductLocations
}

export function SizesBottomSheetRef(
	{
		product,
		productId,
		selectedColorId,
		'data-testid': dataTestId,
		onClose,
		onSelectedSize,
		onSizeGuideLink,
		hasSizesGuide,
		...extended
	}: Readonly<SheetProps | SheetExtendedProps>,
	ref: Ref<DialogAPI | null>
) {
	const { t } = useLabels()
	const stepsRef = useRef<StepsApi<BackInStockProps | null>>(null)
	const bottomSheetRef = useRef<DialogAPI>(null)

	const { disclaimerType } = useMessageDisclaimer({ product })

	const { backInStockModalProps } = useBackInStock()

	useImperativeHandle(ref, () => bottomSheetRef.current as DialogAPI, [])

	const selectedSizeHandler = useSelectedSize(
		onSelectedSize,
		productId,
		selectedColorId,
		product,
		bottomSheetRef
	)

	useEffect(() => {
		if (backInStockModalProps && stepsRef.current?.ctx) {
			stepsRef.current.ctx.detail = backInStockModalProps
			stepsRef.current.next()
		}
	}, [backInStockModalProps])

	const { delayedMessage, delayedWareHouse, productStock, location } =
		extended as SheetExtendedProps

	return (
		<BottomSheet
			data-testid={dataTestId}
			title={t('productCard.selectSize.title')}
			ref={bottomSheetRef}
			onClose={onClose}
		>
			<Steps ref={stepsRef}>
				{(_, prev) => [
					<Fragment key='step1'>
						<Header
							title={t('productCard.selectSize.title')}
							alternativeAction={
								hasSizesGuide ? (
									<ButtonContentLink onClick={onSizeGuideLink}>
										{t('sizeGuide.link')}
									</ButtonContentLink>
								) : undefined
							}
						/>
						<Content hasPadding={false}>
							{disclaimerType && <DisclaimerMessage type={disclaimerType} />}
							<SizesList
								productStock={productStock}
								product={product}
								location={location}
								productId={productId}
								selectedColorId={selectedColorId}
								onSelectedSize={selectedSizeHandler}
								delayedMessage={delayedMessage}
								delayedWareHouse={delayedWareHouse}
							/>
						</Content>
					</Fragment>,
					<BackInStockForm key='step2' prev={prev} />,
				]}
			</Steps>
		</BottomSheet>
	)
}

export const SizesBottomSheet = forwardRef(SizesBottomSheetRef)
